<script lang="ts">
	// The design system's Band (design/components/surfaces/Band.jsx): a notice
	// that is part of the page's composition and lives as long as the page
	// does, unlike Notice, which is defined by when it leaves. An optional
	// badge at the inline start, the message, an optional meta and an action.
	//
	// flag is the amber "something you need to know"; ink is the emphasis
	// surface. The app adds test ids, and a busy action that stops answering
	// while its work is in flight but keeps looking live.

	import type { Snippet } from 'svelte';
	import { pressable } from './pressable';

	interface Props {
		tone?: 'flag' | 'ink';
		badge?: string;
		badgeSize?: 'lg' | 'md';
		action?: string;
		onAction?: () => void;
		actionBusy?: boolean;
		meta?: string;
		testId?: string;
		actionTestId?: string;
		/** Read out at once, as the design passes role="alert" for a refusal. */
		alert?: boolean;
		children: Snippet;
	}

	let {
		tone = 'flag',
		badge,
		badgeSize = 'lg',
		action,
		onAction,
		actionBusy = false,
		meta,
		testId,
		actionTestId,
		alert = false,
		children,
	}: Props = $props();
</script>

<div class="band {tone}" data-testid={testId} role={alert ? 'alert' : undefined}>
	{#if badge}
		<span class="badge {badgeSize}">{badge}</span>
	{/if}
	<p class="message">{@render children()}</p>
	{#if meta}
		<span class="meta">{meta}</span>
	{/if}
	{#if action}
		<button
			type="button"
			class="action"
			data-press
			data-testid={actionTestId}
			aria-busy={actionBusy || undefined}
			onclick={actionBusy ? undefined : onAction}
			{@attach pressable}
		>
			{action}
		</button>
	{/if}
</div>

<style>
	.band {
		border-radius: var(--radius-panel);
		padding: var(--card-pad);
		display: flex;
		flex-wrap: wrap;
		gap: 16px;
		align-items: center;
	}

	.flag {
		background: var(--surface-flag);
		border: var(--border-width-strong) solid var(--border-flag);
		--band-text: var(--ink-flag-deep);
		--band-badge-bg: var(--surface-flag-solid);
		--band-badge-fg: var(--on-flag);
		--band-action-bg: var(--surface-flag-solid);
		--band-action-fg: var(--on-flag);
		--band-meta: var(--ink-flag);
	}

	.ink {
		background: var(--surface-invert);
		border: none;
		--band-text: var(--on-ink);
		--band-badge-bg: var(--speed);
		--band-badge-fg: var(--on-speed);
		--band-action-bg: var(--surface-card);
		--band-action-fg: var(--text-strong);
		--band-meta: var(--on-ink-muted);
	}

	.badge {
		background: var(--band-badge-bg);
		color: var(--band-badge-fg);
		flex: none;
		white-space: nowrap;
		font-weight: 700;
		border-radius: var(--radius-tag);
	}

	.badge.lg {
		font-size: 17px;
		padding: 8px 18px;
	}

	.badge.md {
		font-size: 15px;
		padding: 7px 15px;
	}

	.message {
		margin: 0;
		flex: 1 1 260px;
		font-size: 16px;
		line-height: 1.7;
		color: var(--band-text);
	}

	.meta {
		flex: none;
		font-family: var(--font-mono);
		font-size: 12.5px;
		color: var(--band-meta);
	}

	.action {
		background: var(--band-action-bg);
		color: var(--band-action-fg);
		flex: none;
		border: none;
		cursor: pointer;
		font-family: var(--font-sans);
		font-size: 14.5px;
		font-weight: 600;
		min-block-size: var(--ctl-min);
		padding: 11px 17px;
		border-radius: var(--radius-btn);
		white-space: nowrap;
	}

	.action[aria-busy='true'] {
		cursor: progress;
	}
</style>
